import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { resolveAvatar } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, StatChip, ProgressBar, DynIcon, RARITY } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import * as Icons from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, setUser } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["profile", user?.id], queryFn: async () => (await api.get(`/students/${user.id}/profile`)).data });
  if (isLoading || !data) return <Loader />;
  const s = data.stats;

  const uploadPic = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data: updated } = await api.put("/me/avatar", { avatar: up.data.url });
      setUser(updated);
      qc.invalidateQueries({ queryKey: ["profile"] });
      toast.success("📸 Profile photo updated!");
    } catch (err) { toast.error("Oops! Photo upload failed. Please try again."); }
    finally { setUploading(false); }
  };

  const share = () => {
    const txt = `🌟 ${user.name}'s Story Champs Progress\nLevel: ${s.level_name}\nStory Points: ${s.points}\nBadges: ${s.badges_count}\nAttendance: ${s.attendance_pct}%\nQuests: ${s.quests_done}\nPerformance Ready: ${s.performance_readiness}%`;
    navigator.clipboard?.writeText(txt);
    toast.success("Progress summary copied — share with parents! 📋");
  };

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div className="rounded-3xl gradient-purple p-6 text-white flex flex-col items-center text-center sm:flex-row sm:text-left sm:gap-6">
        <div className="relative">
          <img src={resolveAvatar(user.avatar)} alt="" className="h-24 w-24 rounded-3xl object-cover ring-4 ring-white/30" />
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPic} data-testid="avatar-file-input" />
          <button data-testid="change-avatar-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="absolute -bottom-1 -right-1 flex h-9 w-9 items-center justify-center rounded-full bg-csh-orange text-white shadow-md ring-2 ring-white hover:scale-110 active:scale-95 transition-transform">
            {uploading ? <Icons.Loader2 size={16} className="animate-spin" /> : <Icons.Camera size={16} />}
          </button>
        </div>
        <div className="mt-3 sm:mt-0 flex-1">
          <h1 className="font-head text-4xl">{user.name}</h1>
          <p className="text-white/80">{user.grade}{user.gender ? ` · ${user.gender}` : ""} · {s.level_name}</p>
          <div className="mt-3 flex flex-wrap justify-center gap-2 sm:justify-start">
            <span className="rounded-full bg-white/15 px-3 py-1 text-sm font-bold">🔥 {s.streak} day streak</span>
            <span className="rounded-full bg-csh-orange px-3 py-1 text-sm font-bold">Level {s.level_index + 1}</span>
          </div>
        </div>
        <Button data-testid="share-progress-btn" onClick={share} className="mt-3 sm:mt-0 rounded-full bg-white text-csh-purple font-bold hover:scale-105">
          <Icons.Share2 size={16} /> Share Progress
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatChip icon="Star" value={s.points} label="Story Points" color="#EAB308" />
        <StatChip icon="Medal" value={s.badges_count} label="Badges" color="#662D91" />
        <StatChip icon="Target" value={s.quests_done} label="Quests" color="#FC633B" />
        <StatChip icon="Mic" value={s.stories_count} label="Stories" color="#22C55E" />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-3xl bg-white p-6 shadow-sm border border-black/5">
          <h3 className="font-head text-xl text-csh-blue mb-2">Attendance</h3>
          <p className="text-3xl font-head text-csh-orange">{s.sessions_attended}/{s.total_sessions}</p>
          <ProgressBar pct={s.attendance_pct} className="mt-2" />
          <p className="mt-1 text-sm text-muted-foreground">{s.attendance_pct}% attendance</p>
        </div>
        <div className="rounded-3xl bg-white p-6 shadow-sm border border-black/5">
          <h3 className="font-head text-xl text-csh-blue mb-2">Performance Readiness</h3>
          <p className="text-3xl font-head text-csh-purple">{s.performance_readiness}%</p>
          <ProgressBar pct={s.performance_readiness} className="mt-2" />
        </div>
      </div>

      <div className="rounded-3xl bg-white p-6 shadow-sm border border-black/5">
        <h3 className="font-head text-xl text-csh-blue mb-3">My Achievements</h3>
        {data.badges.length === 0 ? <p className="text-sm text-muted-foreground">No badges yet — complete quests to earn them!</p> : (
          <div className="flex flex-wrap gap-3">
            {data.badges.map((b) => { const r = RARITY[b.badge?.rarity] || RARITY.common; return (
              <div key={b.id} className="flex flex-col items-center w-20 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full" style={{ backgroundColor: (b.badge?.color || "#662D91") + "22", border: `3px solid ${b.badge?.color || "#662D91"}` }}>
                  <DynIcon name={b.badge?.icon} size={24} color={b.badge?.color || "#662D91"} />
                </div>
                <span className="mt-1 text-[10px] font-bold text-csh-blue leading-tight">{b.badge?.name}</span>
              </div>
            ); })}
          </div>
        )}
      </div>
    </div>
  );
}
